/**
 * SankeyDiagram — Shows flow from tax revenues to government expenditures.
 * Uses Bklit SankeyChart for rendering.
 */
import {
  SankeyChart,
  SankeyLink,
  SankeyNode,
  SankeyTooltip,
  type SankeyData,
} from '@/components/charts/sankey'
import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '@/store'
import { fetchTaxRevenues, fetchCofogExpenditures } from '@/data/eurostat'

// Human-readable labels
const TAX_LABELS: Record<string, string> = {
  D211: 'TVA',
  D212: 'Droits de douane',
  D214: 'TIPP, alcools, tabac…',
  D29:  'Taxe foncière, CFE…',
  D51A: 'Impôt sur le revenu',
  D51B: 'Impôt sur les sociétés',
  D59:  'IFI, succession…',
  D611: 'Cotisations employeurs',
  D612: 'Cotisations salariés',
  D613: 'Cotisations indépendants',
}

const EXP_LABELS: Record<string, string> = {
  GF01: 'Services de l\'État',
  GF02: 'Défense',
  GF03: 'Police, justice',
  GF04: 'Économie, emploi',
  GF05: 'Environnement',
  GF06: 'Logement',
  GF07: 'Santé',
  GF08: 'Culture, sports',
  GF09: 'Éducation',
  GF10: 'Retraites, chômage',
}

const TAX_COLORS: Record<string, string> = {
  D211: '#e94560',
  D214: '#fdcb6e',
  D29:  '#ffeaa7',
  D51A: '#00b894',
  D51B: '#00cec9',
  D59:  '#81ecec',
  D611: '#6c5ce7',
  D612: '#a29bfe',
  D613: '#dfe6e9',
}

const EXP_COLORS: Record<string, string> = {
  GF01: '#636e72',
  GF02: '#2d3436',
  GF03: '#74b9ff',
  GF04: '#00cec9',
  GF05: '#55efc4',
  GF06: '#ffeaa7',
  GF07: '#ff7675',
  GF08: '#a29bfe',
  GF09: '#0984e3',
  GF10: '#e17055',
}

// Map tax codes to expense categories for the Sankey links
const TAX_TO_EXPENSE_LINKS: Record<string, string[]> = {
  D211: ['GF04', 'GF10'],
  D214: ['GF04', 'GF05'],
  D29:  ['GF01', 'GF02', 'GF03'],
  D51A: ['GF01', 'GF10', 'GF07'],
  D51B: ['GF01', 'GF04', 'GF02'],
  D59:  ['GF01'],
  D611: ['GF10', 'GF07', 'GF09'],
  D612: ['GF10', 'GF07', 'GF09'],
  D613: ['GF10', 'GF07'],
}

export default function SankeyDiagram() {
  const { selectedYear } = useAppStore()

  const taxesQuery = useQuery({
    queryKey: ['taxes', selectedYear],
    queryFn: () => fetchTaxRevenues(selectedYear),
    staleTime: 1000 * 60 * 60,
  })

  const expQuery = useQuery({
    queryKey: ['cofog', selectedYear],
    queryFn: () => fetchCofogExpenditures(selectedYear),
    staleTime: 1000 * 60 * 60,
  })

  if (taxesQuery.isLoading || expQuery.isLoading) {
    return (
      <div className="h-96 flex items-center justify-center text-text-muted">
        Chargement des flux…
      </div>
    )
  }

  const taxes = taxesQuery.data ?? []
  const expenditures = expQuery.data ?? []

  if (taxes.length === 0 || expenditures.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-text-muted text-sm">
        Données indisponibles pour {selectedYear}
      </div>
    )
  }

  // Build nodes with simple name labels and color metadata
  const nodes = [
    ...taxes.map((t) => ({
      name: `${TAX_LABELS[t.code] ?? t.shortLabel} (${(t.value / 1000).toFixed(0)} Md€)`,
      color: TAX_COLORS[t.code] ?? '#ccc',
      category: 'source' as const,
    })),
    ...expenditures.map((e) => ({
      name: `${EXP_LABELS[e.code] ?? e.label} (${(e.value / 1000).toFixed(0)} Md€)`,
      color: EXP_COLORS[e.code] ?? '#ccc',
      category: 'outcome' as const,
    })),
  ]

  // Build index maps: tax/exp code → position in the nodes array
  const taxIndexMap = new Map(taxes.map((t, i) => [t.code, i]))
  const expIndexMap = new Map(
    expenditures.map((e, i) => [e.code, taxes.length + i]),
  )

  // Build links using index-based source/target references
  const links: Array<{
    source: number
    target: number
    value: number
    color: string
  }> = []

  for (const tax of taxes) {
    const targets = TAX_TO_EXPENSE_LINKS[tax.code] ?? ['GF01']
    const totalExpValue = targets.reduce(
      (sum, code) =>
        sum + (expenditures.find((e) => e.code === code)?.value ?? 0),
      0,
    )

    for (const expCode of targets) {
      const expValue =
        expenditures.find((e) => e.code === expCode)?.value ?? 0
      const fraction =
        totalExpValue > 0 ? expValue / totalExpValue : 1 / targets.length
      const value = Math.round(tax.value * fraction)

      const sourceIdx = taxIndexMap.get(tax.code)
      const targetIdx = expIndexMap.get(expCode)

      if (value > 1000 && sourceIdx !== undefined && targetIdx !== undefined) {
        links.push({
          source: sourceIdx,
          target: targetIdx,
          value,
          color: TAX_COLORS[tax.code] ?? '#ccc',
        })
      }
    }
  }

  const data: SankeyData = { nodes, links }

  return (
    <SankeyChart
      data={data}
      aspectRatio="3 / 2"
      margin={{ top: 20, right: 180, bottom: 20, left: 180 }}
      nodeWidth={18}
      nodePadding={24}
    >
      <SankeyLink
        getLinkColor={(_, index) => links[index]?.color ?? '#ccc'}
        strokeOpacity={0.35}
      />
      <SankeyNode
        getNodeColor={(_, index) => nodes[index]?.color as string ?? '#ccc'}
        showLabels
      />
      <SankeyTooltip
        formatValue={(v) => `${(v / 1000).toFixed(0)} Md€`}
      />
    </SankeyChart>
  )
}
